import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationResult,
  EndSessionPopupRequest,
  InteractionType,
  PopupRequest,
  PublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';
import { MsalConfig } from '@core/authentification/msal.config';
import { MsalConfigDto } from '@interfaces/MsalConfigDto';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class AuthService {
  private startedResolve: (value?: unknown) => void;

  private startedPromise = new Promise(resolve => {
    this.startedResolve = resolve;
  });

  private msalInstance: PublicClientApplication;
  private msalConfig: MsalConfigDto;
  public get isMsalConfigured(): boolean {
    return !!this.msalConfig;
  }

  public constructor(private router: Router, private http: HttpClient, private logger: NGXLogger) {}

  public async start() {
    this.msalConfig = await this.getConfig();

    this.msalInstance = MsalConfig.createInstance(this.msalConfig, this.logger);

    const redirectResult = await this.msalInstance.handleRedirectPromise(window.location.hash);
    if (redirectResult?.account) {
      this.msalInstance.setActiveAccount(redirectResult.account);
    }
    
    this.logger.info('Startup complete');
    this.startedResolve();

  }

  public async acquireAccessToken(request: SilentRequest, options?: { disableUserInteraction: boolean }) {
    try {
      this.logger.debug('Try acquireTokenSilent');

      const result = await this.msalInstance.acquireTokenSilent(request);
      return result?.accessToken;
    } catch (err) {
      if (options?.disableUserInteraction) {
        this.logger.error('Silent Token aquisition failed');
        return undefined;
      }

      this.logger.warn('Silent Token aquisition failed try interactive');

      const promise =
        MsalConfig.PreferredInteractionType === InteractionType.Popup
          ? this.msalInstance.acquireTokenPopup(request)
          : this.msalInstance.acquireTokenRedirect(request);

      const result = await promise;

      return (result as AuthenticationResult)?.accessToken;
    }
  }

  private getTokenPromise?: Promise<string | undefined>;

  public async getToken() {
    if (this.getTokenPromise === undefined) {
      this.getTokenPromise = this._getToken();
    }

    const result = await this.getTokenPromise;

    this.getTokenPromise = undefined;
    return result;
  }

  private async _getToken() {
    await this.startedPromise;
    let account = this.getAccount();

    if (!account) {
      account = (await this.login()) ?? undefined;
    }

    let token: string | undefined;
    try {
      token = await this.acquireAccessToken({
        account,
        scopes: this.msalConfig.scopes
      });
    } catch (err) {
      this.logger.error('POP: Failed to retrieve token!');
      this.logger.error(err);
      return undefined;
    }

    return token;
  }

  public getAccount() {
    const activeAccount = this.msalInstance.getActiveAccount();

    if (activeAccount) {
      return activeAccount;
    }
    const firstAccount = this.msalInstance.getAllAccounts()[0];
    if (firstAccount) {
      this.msalInstance.setActiveAccount(firstAccount);
      return firstAccount;
    }
    return undefined;
  }

  public async login() {
    const request: PopupRequest = {
      scopes: this.msalConfig.scopes
    };
    if (MsalConfig.PreferredInteractionType === InteractionType.Redirect) {
      await this.msalInstance.loginRedirect(request);
    }

    const auntenticationResult = await this.msalInstance.loginPopup(request);

    return auntenticationResult?.account;
  }

  public async logout() {
    const params: EndSessionPopupRequest = {
      account: this.getAccount()
    };
    if (MsalConfig.PreferredInteractionType === InteractionType.Redirect) {
      await this.msalInstance.logoutRedirect(params);
    }
    await this.msalInstance.logoutPopup(params);

    await this.router.navigate(['/logout']);
  }

  private getConfig() {
    return firstValueFrom(this.http.get<MsalConfigDto>(`/assets/msal-config.json?t=${Date.now()}`));
  }
}
